import React, { useCallback } from "react"
import { Box, Flex } from "@theme-ui/components"
import { navigate, Link } from "gatsby"
import { StaticImage, GatsbyImage } from "gatsby-plugin-image"
import { useThemeUI } from "theme-ui"

import Player from "./player"

const FeaturedCaseTile = ({
  hoverCase,
  caseCount,
  index,
  activeCases,
  active,
  isGridView,
  featuredCase,
  prefix,
}) => {
  const { colorMode } = useThemeUI()
  const caseToShow = hoverCase || featuredCase
  const previewFormat = caseToShow.previewFormat
    ? caseToShow.previewFormat[0]
    : ""

  const props = {
    display: active ? "flex" : "none !important",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100%",
    justifyContent: previewFormat == "16:9 fullwidth" ? "flex-start" : "center",
    minHeight: "100vh",
  }

  const canShowCase = useCallback(() => {
    return (
      caseToShow.vimeoId ||
      (caseToShow.indexStills && caseToShow.indexStills.length)
    )
  }, [hoverCase, featuredCase])

  if (isGridView) {
    props.maxWidth = "100%"
  } else {
    switch (activeCases) {
      case "Design":
        if (previewFormat == "16:9 fullwidth") {
          props.maxWidth = "100%"
        } else if (previewFormat == "16:9") {
          props.maxWidth = "800px"
        } else {
          props.maxWidth = "530px"
        }
        break
      case "Stills":
        props.maxWidth = "800px"
        props.paddingLeft = "50px"
        props.paddingRight = "50px"
        break
      default:
        props.maxWidth = "800px"
        break
    }
  }

  const handleRenderCaseContent = () => {
    const aspRatio = previewFormat.includes("16:9") ? "16/9" : "1/1"
    switch (activeCases) {
      case "Design":
        return (
          <Box
            sx={{
              aspectRatio: aspRatio,
              width: "100%",
            }}
          >
            <img
              style={{
                aspectRatio: aspRatio,
                objectFit: "cover",
                objectPosition: "center",
              }}
              width="100%"
              src={caseToShow.mainContent.file.url}
            />
          </Box>
        )

      case "Stills":
        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gridColumnGap: "10px",
              height: ["185px", "373px", "373px", "373px", "450px"],
              overflow: "hidden",
            }}
          >
            {caseToShow.indexStills?.map((s) => {
              return (
                <GatsbyImage
                  image={s.gatsbyImageData}
                  key={s.id}
                  height="100%"
                  flex="1"
                  alt=""
                />
              )
            })}
          </Box>
        )
      default:
        return (
          <Player
            link={isGridView}
            to={`${prefix}cases/${caseToShow.slug}/`}
            key={caseToShow.slug}
            tempImage={caseToShow.thumbnail}
            thumbnail={caseToShow.thumbnail}
            small={isGridView}
            id={caseToShow.slug}
            aspectRatio={"2.39:1"}
            vimeoId={caseToShow.vimeoId}
          />
        )
    }
  }

  return (
    <Flex
      id={caseToShow.slug}
      sx={!isGridView ? props : { flexDirection: "column" }}
    >
      {!isGridView && previewFormat != "16:9 fullwidth" && (
        <Flex
          mb={2}
          sx={{ fontSize: 1, width: "100%", justifyContent: "flex-end" }}
        >
          <Box>
            (&#8201;{index + 1}&#8201;/&#8201;
            {caseCount}&#8201;)
          </Box>
        </Flex>
      )}
      <Box
        sx={{
          width: "100%",
        }}
      >
        {handleRenderCaseContent()}
      </Box>
      <Flex
        sx={{
          cursor: canShowCase() ? "pointer" : "default",
          justifyContent: "space-between",
        }}
        onClick={() => {
          if (canShowCase()) {
            navigate(`${prefix}cases/${caseToShow.slug}/`)
          }
        }}
        px={isGridView && "9px"}
        py={isGridView ? "11px" : "40px"}
      >
        <Box
          sx={{
            a: {
              textDecoration: "none",
            },
          }}
        >
          <Link to={`${prefix}cases/${caseToShow.slug}/`}>
            <Box>
              {!isGridView && (
                <Box
                  sx={{
                    fontFamily: "pragmatica-extended",
                    fontWeight: "200",
                    color: "darkest",
                    textTransform: "uppercase",
                  }}
                >
                  Case
                </Box>
              )}
              <Box
                sx={{
                  fontFamily: "pragmatica-extended",
                  color: "darkest",
                  textTransform: "uppercase",
                  h1: {
                    fontWeight: "400",
                    fontSize: "12px",
                    my: "0",
                    lineHeight: "1.5em",
                  },
                }}
              >
                <h1>{caseToShow.clientName}</h1>
              </Box>
            </Box>
            <Box mt={"5px"} mb={40}>
              <Box
                sx={{
                  color: "darkest",
                  fontSize: "10px",
                }}
              >
                {caseToShow.title}
              </Box>
              {!isGridView && activeCases === "Stills" && (
                <Box
                  sx={{
                    color: "darkest",
                    fontSize: "10px",
                  }}
                >
                  {caseToShow.byLine}
                </Box>
              )}
              {!isGridView && (
                <Box
                  sx={{
                    color: "darkest",
                    fontSize: "10px",
                  }}
                >
                  {caseToShow.caseType}
                </Box>
              )}
            </Box>
          </Link>
        </Box>
        {!isGridView && previewFormat != "16:9 fullwidth" && (
          <Box sx={{ maxWidth: 250, width: "100%", height: "100%" }}>
            <Flex>
              <Box
                sx={{
                  flex: 1,
                  marginLeft: "2px",
                  height: "3px",
                  backgroundColor: caseToShow.color1,
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  marginLeft: "2px",
                  height: "3px",
                  backgroundColor: caseToShow.color2,
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  marginLeft: "2px",
                  height: "3px",
                  backgroundColor: caseToShow.color3,
                }}
              />
            </Flex>
            <Box sx={{ textAlign: "right" }} mt="1rem">
              <Box
                sx={{
                  fontSize: "20px",
                  textTransform: "uppercase",
                  a: {
                    textDecoration: "none",
                  },
                }}
              >
                <Link to={`${prefix}cases/${caseToShow.slug}/`}>
                  <Flex
                    sx={{ justifyContent: "flex-end", alignItems: "center" }}
                  >
                    <Box
                      mr={3}
                      sx={{
                        fontFamily: "pragmatica-extended, sans-serif",
                        fontSize: "16px",
                        fontWeight: "300",
                        display: "inline-block",
                        color: "darkest",
                      }}
                    >
                      See Case
                    </Box>
                    <Box
                      sx={{
                        height: "16px",
                        filter:
                          colorMode == "midnight" ? "invert(1)" : "invert(0)",
                      }}
                    >
                      <img height="100%" src={"/assets/caret.png"} />
                    </Box>
                  </Flex>
                </Link>
              </Box>
            </Box>
          </Box>
        )}
      </Flex>
    </Flex>
  )
}

export default FeaturedCaseTile
